import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { debounce } from "lodash";
import { formatDateToUtc } from "../../../utils/formatDate";
import { useSelector } from "react-redux";
import { getConceptFilter } from "../../../utils/getConceptFilter";

import SmallMultiple from "./SmallMultiple";
import LabelSelector from "./LabelSelector"; // Common label selector

export const ShareOfVoiceOverTime = (props) => {
  const [conceptData, setConceptData] = useState({});
  const [loading, setLoading] = useState(false);
  const [getVolume] = useGetConceptsFiltersMutation();

  // State for unique x-axis labels (e.g., "Q1 2023", etc.)
  const [allLabels, setAllLabels] = useState([]);
  // State for currently selected labels.
  const [selectedLabels, setSelectedLabels] = useState([]);

  const labelFontSize = useSelector((state) => {
    const fontSizes = state.fontManager.fontSizes;
    const normalizedKey = Object.keys(fontSizes).find(
      (key) => key.trim() === "labelFontSize"
    );
    return fontSizes[normalizedKey] || 16;
  });

  const {
    selectedDateName,
    interval,
    id,
    filters,
    conceptList,
    visualizeType,
    setFilterDocCount,
    setLocalConceptOptions,
    seletctedType,
    widget,
  } = props;

  const conceptFilter = useMemo(
    () => JSON.stringify(getConceptFilter(props)),
    [props]
  );

  const getVolumeData = useCallback(async () => {
    setLoading(true);

    const { data: dataConcepts } = await getVolume({
      id,
      concept_selector: JSON.parse(conceptFilter),
      filter: filters,
      breakdowns: [{ name: selectedDateName, interval }],
    });

    setFilterDocCount(dataConcepts.filter_count);
    if (conceptList) {
      setLocalConceptOptions(dataConcepts.match_counts);
    }

    const breakdownData = dataConcepts.breakdowns[0].buckets;
    const conceptsMap = {};

    breakdownData.forEach(({ label, match_counts, filter_count, total_count }) => {
      if (!filter_count) return;
      const formattedDate = formatDateToUtc(label, interval);
      match_counts.forEach(({ name, match_count, exact_match_count }) => {
        if (!conceptsMap[name]) {
          conceptsMap[name] = [];
        }

        // Determine the count based on selected type.
        let count = match_count;
        switch (seletctedType) {
          case "exact_match_count":
            count = exact_match_count;
            break;
          case "conceptual_match_count":
            count = match_count - exact_match_count;
            break;
          default:
            count = match_count;
            break;
        }

        // Calculate percentage:
        // If visualizeType is true, use the bucket's total_count; otherwise, use dataConcepts.total_count.
        const percentage = visualizeType
          ? total_count
            ? ((count / total_count) * 100).toFixed(1)
            : "0"
          : dataConcepts.total_count
          ? ((count / dataConcepts.total_count) * 100).toFixed(1)
          : "0";

        // Determine metric key: when visualizing percentages, use a "%" suffix.
        const metricKey = visualizeType ? name : `${name} %`;

        // Save both percentage and raw count.
        conceptsMap[name].push({
          date: formattedDate,
          [metricKey]: Number(percentage),
          [`${metricKey}_count`]: count,
        });
      });
    });

    setConceptData(conceptsMap);

    // Extract all unique x-axis labels.
    const labelSet = new Set();
    Object.values(conceptsMap).forEach((dataPoints) => {
      dataPoints.forEach((dp) => {
        if (dp.date) {
          labelSet.add(dp.date);
        }
      });
    });
    const labels = Array.from(labelSet);
    // Sort labels (assuming format like "Q1 2023")
    labels.sort((a, b) => {
      const yearA = a.match(/\d{4}/) ? parseInt(a.match(/\d{4}/)[0], 10) : 0;
      const yearB = b.match(/\d{4}/) ? parseInt(b.match(/\d{4}/)[0], 10) : 0;
      if (yearA !== yearB) return yearA - yearB;
      const quarterA = a.match(/Q(\d)/) ? parseInt(a.match(/Q(\d)/)[1], 10) : 0;
      const quarterB = b.match(/Q(\d)/) ? parseInt(b.match(/Q(\d)/)[1], 10) : 0;
      return quarterA - quarterB;
    });
    setAllLabels(labels);
    // Initially, select all labels.
    setSelectedLabels(labels);

    setLoading(false);
  }, [
    conceptFilter,
    conceptList,
    filters,
    getVolume,
    id,
    interval,
    selectedDateName,
    seletctedType,
    setFilterDocCount,
    setLocalConceptOptions,
    visualizeType,
  ]);

  useEffect(() => {
    const debouncedGetVolume = debounce(getVolumeData, 1000);
    debouncedGetVolume();
    return () => {
      debouncedGetVolume.cancel();
    };
  }, [getVolumeData]);

  // Toggle a label (date) on/off.
  const handleToggleLabel = (label) => {
    setSelectedLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  // Filter each concept's data based on selected x-axis labels.
  const filteredConceptData = {};
  Object.entries(conceptData).forEach(([conceptName, dataPoints]) => {
    filteredConceptData[conceptName] = dataPoints.filter((dp) =>
      selectedLabels.includes(dp.date)
    );
  });

  if (loading) {
    return (
      <Spin
        className="spinner-by-center"
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      />
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Charts Grid */}
      <div style={{ flex: "1 1 auto" }}>
        <div
          className="share-of-voice-multiples"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1rem",
            padding: "0.5rem",
          }}
        >
          {Object.keys(filteredConceptData).map((conceptName, idx) => {
            const data = filteredConceptData[conceptName];
            const metricKey = visualizeType ? conceptName : `${conceptName} %`;
            // Use a fixed color palette.
            const colorPalette = [
              "#A5D8FF",
              "#FFC9A8",
              "#FDFFA1",
              "#D2FF99",
              "#E6CCFF",
              "#FFB0C9",
              "#99FFEE",
              "#D5FF99",
              "#FFF59B",
              "#C4C4C4",
            ];
            const color = colorPalette[idx % colorPalette.length];
            return (
              <div
                key={conceptName}
                style={{
                  flex: "0 0 356px",
                  height: "220px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <SmallMultiple data={data} metric={metricKey} color={color} title={conceptName} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Common Label Selector for x-axis */}
      <div style={{ flex: "0 0 auto", padding: "0.5rem 1rem" }}>
        {allLabels.length > 0 && (
          <LabelSelector
            labels={allLabels}
            selectedLabels={selectedLabels}
            onToggleLabel={handleToggleLabel}
          />
        )}
      </div>
    </div>
  );
};

export default ShareOfVoiceOverTime;
